import React, { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import moment from './moment'
import DateStepper from './DateStepper'
import { useNavigation, useStartOfDay } from './utils'
import { classNames, useSetState } from '@zambezi/sdk/common'

/**
 * Create a React Year Picker component
 *
 * @param {import('./interfaces').YearPickerProps} props
 * @returns {JSX.Element}
 */
function YearPicker ({
  isPrevYearDisabled,
  isNextYearDisabled,
  yearSteps,
  idPrefix,
  year: propsYear,
  defaultYear,
  displayedDate,
  withKeyboardNavigation,
  isYearSelected,
  isYearDisabled,
  onYearChange,
  onYearSelected,
  onFocusChanged,
  onFocusedYearRef
}) {
  const [{ year, focusedYear }, setState] = useSetState({
    year: propsYear === undefined ? defaultYear : propsYear,
    focusedYear: displayedDate == null ? null : displayedDate.getFullYear()
  })
  const onNavigationRef = useRef()
  const elementRef = useRef()
  const focusedYearRef = useRef()

  useEffect(updateFocusedYearRef, [focusedYearRef.current, focusedYear])
  useEffect(updateNavigation)
  useEffect(updateYear, [propsYear])
  useNavigation(onNavigationRef, elementRef)

  const now = useStartOfDay()
  const currentYear = now.getFullYear()
  const years = []
  for (let i = -yearSteps; i <= yearSteps; i++) {
    years.push(renderYear(i + (focusedYear || year), i))
  }

  return (
    <div className='zb-month-picker zb-year-picker'>
      <section className='zb-month-picker-header'>
        <DateStepper
          date={new Date(focusedYear || year, 0, 1, 0, 0)}
          stepValue={yearSteps * 2 + 1}
          renderLabel={d => d.getFullYear()}
          onPrevDate={handleYearChange}
          onNextDate={handleYearChange}
          prevDisabled={isPrevYearDisabled(year - yearSteps - 1)}
          nextDisabled={isNextYearDisabled(year + yearSteps + 1)}
        />
      </section>
      <section
        className='zb-month-calendar'
        role='grid'
        aria-describedby={`${idPrefix}-year-calendar-description`}
        aria-label='Years'
        ref={elementRef}
      >
        <span
          id={idPrefix ? `${idPrefix}-year-calendar-description` : null}
          className='zb-slider-description-sr-only'
        >
          You can browse through the calendar table
        </span>
        <section
          className='zb-month-calendar-months zb-year-calendar-years'
          role='row'
        >
          {years}
        </section>
      </section>
    </div>
  )

  function updateYear () {
    if (propsYear !== undefined) {
      setState({ year: propsYear, focusedYear: null })
    }
  }

  function updateNavigation () {
    onNavigationRef.current = withKeyboardNavigation ? onNavigation : null
  }

  function renderYear (year, index) {
    const selected = isYearSelected(year) || null
    const focused = focusedYear === year
    const disabled = isYearDisabled(year) || null
    const id = idPrefix && `${idPrefix}-${year}`
    const tabIndex = index === 0 ? 0 : -1

    let label = year
    if (currentYear === year) label += ' current year'
    if (selected) label += ' selected year'

    return (
      <button
        type='button'
        role='gridcell'
        className={classNames('zb-month-calendar-month zb-year-calendar-year', {
          'zb-month-calendar-month-is-selected': selected,
          'zb-month-calendar-month-is-disabled': disabled,
          'zb-month-calendar-month-is-focused': focused,
          'zb-month-calendar-month-is-current': currentYear === year
        })}
        onClick={handleYearSelected.bind(null, year)}
        aria-disabled={disabled}
        aria-label={label}
        id={id}
        key={year}
        ref={ref => onFocusedYearButtonRef(ref, tabIndex)}
        tabIndex={tabIndex}
      >
        {year}
      </button>
    )
  }

  function handleYearChange (d) {
    const year = d.getFullYear()
    setState({ year })

    onYearChange(year)
  }

  function handleYearSelected (year) {
    if (isYearDisabled(year)) return
    onYearSelected(year)
  }

  function onNavigation (keyCode, inside) {
    const steps = {
      left: -1,
      right: 1,
      up: -3,
      down: 3
    }

    const focusedYearDate = moment(displayedDate || now).year(
      focusedYear == null ? year : focusedYear
    )
    if (keyCode in steps) {
      const newFocusedDate = focusedYearDate.add(steps[keyCode], 'years')
      updateFocusState(
        {
          focusedYear: newFocusedDate.year()
        },
        newFocusedDate.toDate()
      )
    } else {
      switch (keyCode) {
        case 'enter':
          if (inside) handleYearSelected(focusedYear || year)
          break
        case 'end':
          updateFocusState({
            focusedYear: focusedYearDate.add(4, 'years').year()
          })
          break
        case 'home':
          updateFocusState({
            focusedYear: focusedYearDate.add(-4, 'years').year()
          })
          break
        case 'esc':
          handleYearSelected(null)
          break
      }
    }
  }

  function updateFocusState (newState, focusedDate) {
    if (onFocusChanged) {
      const {
        focusedYear: stateFocusedYear = focusedYear,
        year: stateYear = year
      } = newState
      const newYear = stateFocusedYear || stateYear
      onFocusChanged(`${idPrefix}-${newYear}`, focusedDate)
    }
    return setState(newState)
  }

  function onFocusedYearButtonRef (ref, tabIndex) {
    if (focusedYearRef && tabIndex === 0) {
      focusedYearRef.current = ref
    }
  }

  function updateFocusedYearRef () {
    if (onFocusedYearRef) onFocusedYearRef(focusedYearRef)
  }
}

YearPicker.propTypes = {
  displayedDate: PropTypes.instanceOf(Date),
  defaultYear: PropTypes.number,
  year: PropTypes.number,
  idPrefix: PropTypes.string,
  isYearSelected: PropTypes.func,
  isYearDisabled: PropTypes.func,
  onYearChange: PropTypes.func,
  onYearSelected: PropTypes.func,
  isPrevYearDisabled: PropTypes.func,
  isNextYearDisabled: PropTypes.func,
  onFocusChanged: PropTypes.func,
  withKeyboardNavigation: PropTypes.bool,
  yearSteps: PropTypes.number
}

YearPicker.defaultProps = {
  isYearSelected: y => false,
  isYearDisabled: y => false,
  onYearSelected: y => {},
  onYearChange: y => {},
  isPrevYearDisabled: y => false,
  isNextYearDisabled: y => false,
  withKeyboardNavigation: false,
  yearSteps: 4
}

export default YearPicker
